// apps/dashboard/src/hooks/useChat.ts
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { createConversation, getConversation, sendMessageStream } from '../api/conversations';
import { useChatStore } from '../stores/chatStore';
import type { FileReference } from './useFileUpload';
import type { InteractivePrompt } from '@eriko/shared-types';

type ConversationStreamEvent =
  | { type: 'conversation'; conversationId: string }
  | { type: 'status'; message: string }
  | { type: 'token'; content: string }
  | { type: 'artifact'; messageIndex: number; data: Record<string, unknown> }
  | { type: 'fork'; fromConversationId: string; toConversationId: string; toTitle: string }
  | { type: 'interactive_prompt'; messageId: string; prompt: InteractivePrompt }
  | { type: 'error'; message: string };

export function useChat() {
  const messages = useChatStore((s) => s.messages);
  const status = useChatStore((s) => s.status);
  const isStreaming = useChatStore((s) => s.isStreaming);
  const error = useChatStore((s) => s.error);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const sendMessage = useCallback(
    async (message: string, files?: FileReference[]) => {
      // Use getState() to avoid stale closures — standard Zustand pattern for async callbacks
      const store = useChatStore.getState();

      if (store.resumeActive) {
        store.clearResumePlaceholder();
        store.setResumeActive(false);
      }
      // Also clear any RESUME_TIMEOUT error from a prior timed-out run so the
      // send proceeds cleanly.
      if (store.error === 'RESUME_TIMEOUT') {
        store.setError(null);
      }

      // When the user sends chip answers, compose a preview that mirrors the
      // server's synthetic-user-message format so the optimistic bubble isn't
      // empty. The server does the canonical write; this is display-only.
      let optimisticText = message;
      const pendingForPreview = store.pendingAnswers;
      if (!message.trim() && pendingForPreview && Object.keys(pendingForPreview.answers).length > 0) {
        const target = store.messages.find(
          (m) =>
            m.dbMessageId === pendingForPreview.messageId ||
            m.id === pendingForPreview.messageId,
        );
        const prompt = target?.metadata?.interactivePrompt;
        if (prompt) {
          const kind = prompt.kind;
          const lines: string[] = [];
          const skipped: string[] = [];
          for (const row of prompt.rows) {
            const a = pendingForPreview.answers[row.rowId];
            const name = row.subject.split(' — ')[0] ?? row.subject;
            if (!a) {
              skipped.push(name);
              continue;
            }
            const opt = row.options.find((o) => o.slug === a.value);
            const tag = kind === 'merchant_category' ? (a.makeRule ? ' (rule)' : ' (once)') : '';
            lines.push(`${name} → ${opt?.label ?? a.value}${tag}`);
          }
          const parts = [...lines.map((l) => l + '.')];
          if (skipped.length > 0) parts.push(`(skipped: ${skipped.join(', ')}).`);
          optimisticText = parts.join(' ');
        }
      }

      store.addUserMessage(
        optimisticText,
        files?.map((f) => ({ fileName: f.fileName, fileType: f.fileType, fileUrl: f.fileUrl })),
      );
      store.setIsStreaming(true);
      store.setError(null);

      const assistantId = store.startAssistantMessage();

      try {
        // Resolve or create conversation
        let conversationId = useChatStore.getState().conversationId;

        const wasNewConversation = !conversationId;
        if (!conversationId) {
          const conv = await createConversation(message);
          conversationId = conv.id;
          const s = useChatStore.getState();
          s.setConversationId(conversationId);
          // Mark the new conversation as locally hydrated so ChatPage skips
          // the skeleton and suppresses the server-driven store overwrite
          // after we navigate to /chat/:id post-[DONE].
          s.markHydrated(conversationId);
        }

        // Build message body
        const body: {
          content: string;
          triggerAI: boolean;
          attachments?: Array<{ fileKey: string; fileName: string; fileType: string; fileUrl: string }>;
          interactiveAnswers?: {
            messageId: string;
            answers: Array<{ rowId: string; value: string; makeRule: boolean }>;
          };
        } = {
          content: message,
          triggerAI: true,
        };
        if (files?.length) {
          body.attachments = files.map((f) => ({
            fileKey: f.fileKey ?? '',
            documentId: f.documentId,
            fileName: f.fileName,
            fileType: f.fileType,
            fileUrl: f.fileUrl ?? '',
          }));
        }
        const pending = useChatStore.getState().pendingAnswers;
        if (pending && Object.keys(pending.answers).length > 0) {
          body.interactiveAnswers = {
            messageId: pending.messageId,
            answers: Object.entries(pending.answers).map(([rowId, a]) => ({
              rowId,
              value: a.value,
              makeRule: a.makeRule,
            })),
          };

          // Optimistic update: mirror the metadata write the server is about to
          // perform in its pre-agent handler, so the compact card renders the
          // user's picks immediately instead of showing "(skipped)" until a
          // page reload. ChatPage skips re-hydration from the server cache
          // once streaming has started (hydratedForConvId guard), so without
          // this local write the streaming-era store state wins and stays stale.
          const store = useChatStore.getState();
          const target = store.messages.find(
            (m) => m.dbMessageId === pending.messageId || m.id === pending.messageId,
          );
          const promptMd = target?.metadata?.interactivePrompt;
          if (target && promptMd) {
            store.setMessageMetadata(target.id, {
              interactivePrompt: {
                ...promptMd,
                answers: { ...pending.answers },
                submittedAt: new Date().toISOString(),
              },
            });
          }
        }

        const stream = await sendMessageStream(conversationId, body);
        useChatStore.getState().clearPendingAnswers();
        const reader = stream.getReader();

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Parse SSE lines
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const data = line.slice(6).trim();

            if (data === '[DONE]') {
              store.finalizeAssistantMessage(assistantId);
              store.setStatus(null);
              store.setIsStreaming(false);

              // Invalidate the conversation cache so metadata (title/status)
              // refreshes on next read. Do NOT overwrite store.messages from
              // the server here: the store is already canonical via SSE events
              // (token/artifact), and the backend may not have persisted the
              // exchange by the time [DONE] fires — fetching would return stale
              // state and wipe the just-streamed messages from the UI.
              const finalConvId = useChatStore.getState().conversationId;
              if (finalConvId) {
                queryClient.invalidateQueries({ queryKey: ['conversation', finalConvId] });
                // Sync URL only after the first stream completes so the sidebar
                // highlight picks up. Deferring until [DONE] keeps the URL stable
                // during streaming and avoids ChatPage remount mid-stream.
                if (wasNewConversation) {
                  navigate(`/chat/${finalConvId}`, { replace: true });
                }
              }
              return;
            }

            try {
              const event = JSON.parse(data) as ConversationStreamEvent;

              // Re-get state for each event to avoid stale closure
              const s = useChatStore.getState();
              switch (event.type) {
                case 'conversation':
                  s.setConversationId(event.conversationId);
                  break;
                case 'status':
                  s.setStatus(event.message);
                  break;
                case 'token':
                  s.setStatus(null); // Hide status when tokens arrive
                  s.appendToken(assistantId, event.content);
                  break;
                case 'artifact':
                  s.addArtifact({
                    messageIndex: event.messageIndex,
                    type: (event.data as Record<string, unknown>)?.type as string ?? 'unknown',
                    data: event.data,
                  });
                  break;
                case 'fork': {
                  // The AI detected a new topic and forked into a separate
                  // conversation. Pre-fetch the new conversation so cache is
                  // warm (title/status), then navigate once ready. markHydrated
                  // ensures ChatPage skips the skeleton and the data-push
                  // effect won't overwrite the in-progress streaming messages.
                  s.setConversationId(event.toConversationId);
                  s.markHydrated(event.toConversationId);
                  queryClient
                    .prefetchQuery({
                      queryKey: ['conversation', event.toConversationId],
                      queryFn: () => getConversation(event.toConversationId),
                    })
                    .finally(() => {
                      navigate(`/chat/${event.toConversationId}`);
                      queryClient.invalidateQueries({ queryKey: ['conversations'] });
                      queryClient.invalidateQueries({ queryKey: ['conversation', event.fromConversationId] });
                    });

                  break;
                }
                case 'interactive_prompt': {
                  // Target the client-side streaming message (assistantId).
                  // event.messageId is the DB row id — stash it on the message
                  // so the submit POST can use it. Rendering reads metadata.
                  s.setMessageMetadata(assistantId, {
                    interactivePrompt: event.prompt,
                  });
                  s.setMessageDbId(assistantId, event.messageId);
                  break;
                }
                case 'error':
                  s.setError(event.message);
                  break;
              }
            } catch {
              // Skip unparseable lines
            }
          }
        }

        // If stream ended without [DONE], the optimistic assistant bubble may
        // still be empty (server crashed mid-stream, OOMKill, network blip).
        // Drop a hardcoded explanation so the user isn't left staring at an
        // empty bubble.
        const finalizeWithFallback = () => {
          const s = useChatStore.getState();
          const msg = s.messages.find((m) => m.id === assistantId);
          if (msg && msg.content === '') {
            s.appendToken(
              assistantId,
              'Connection lost — please send your message again.',
            );
          }
          s.finalizeAssistantMessage(assistantId);
          s.setIsStreaming(false);
        };
        finalizeWithFallback();
      } catch (err) {
        const s = useChatStore.getState();
        s.setError(err instanceof Error ? err.message : 'Connection failed');
        const msg = s.messages.find((m) => m.id === assistantId);
        if (msg && msg.content === '') {
          s.appendToken(
            assistantId,
            'Connection lost — please send your message again.',
          );
        }
        s.finalizeAssistantMessage(assistantId);
        s.setIsStreaming(false);
      }
    },
    [queryClient, navigate],
  );

  return { messages, status, isStreaming, error, sendMessage };
}
